import { Circle, CircleCheck, CircleDashed, CircleDot, CircleX, Clock } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

import { VideoProjectStatusColumnId } from "./columns";

function InProgressIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 1.75a6.25 6.25 0 0 1 0 12.5Z" fill="currentColor" />
    </svg>
  );
}

interface StatusIconConfig {
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}

const STATUS_ICONS: Record<VideoProject.Status, StatusIconConfig> = {
  [VideoProject.Status.SUBMITTED_FOR_EDITING]: { icon: Circle, className: "text-blue-500" },
  [VideoProject.Status.EDITING]: { icon: InProgressIcon, className: "text-amber-500" },
  [VideoProject.Status.SUBMITTED_FOR_TESTING]: { icon: CircleDashed, className: "text-indigo-500" },
  [VideoProject.Status.TESTING]: { icon: CircleDot, className: "text-violet-500" },
  [VideoProject.Status.READY_TO_POST]: { icon: CircleCheck, className: "text-amber-500" },
  [VideoProject.Status.SCHEDULED]: { icon: Clock, className: "text-purple-500" },
  [VideoProject.Status.POSTED]: { icon: CircleCheck, className: "text-emerald-500" },
  [VideoProject.Status.POST_FAILED]: { icon: CircleX, className: "text-rose-500" },
};

const COLUMN_STATUS_ICONS: Record<VideoProjectStatusColumnId, StatusIconConfig> = {
  submitted_for_editing: STATUS_ICONS[VideoProject.Status.SUBMITTED_FOR_EDITING],
  editing: STATUS_ICONS[VideoProject.Status.EDITING],
  submitted_for_testing: STATUS_ICONS[VideoProject.Status.SUBMITTED_FOR_TESTING],
  testing: STATUS_ICONS[VideoProject.Status.TESTING],
  completed: STATUS_ICONS[VideoProject.Status.READY_TO_POST],
};

export function VideoProjectStatusIcon({
  status,
  columnId,
  className,
}: {
  status?: VideoProject.Status;
  columnId?: VideoProjectStatusColumnId;
  className?: string;
}) {
  const config = (status ? STATUS_ICONS[status] : columnId ? COLUMN_STATUS_ICONS[columnId] : undefined) ?? {
    icon: Circle,
    className: "text-muted-foreground",
  };
  const Icon = config.icon;

  return <Icon className={cn("size-4 shrink-0", config.className, className)} />;
}

export function statusDisplayLabel(status: VideoProject.Status | string, revealInternalStatuses = true) {
  const visibleStatus = revealInternalStatuses ? status : VideoProject.clientStatus(status);
  return VideoProject.StatusMeta[visibleStatus as VideoProject.Status]?.label || visibleStatus;
}
